// frontend/src/utils/contestSummary.ts
//
// The shared contest-identity formatter (decided 2026-07-05): the Scorecard's
// pre-kickoff strip and the Builder's framing header (BAR-FUNNEL step 2) must
// render from ONE derivation so the two surfaces never drift. Source of truth
// for dates = the contest's own matches (the slate IS its matches), falling
// back to the contest start_time before matches load.
//
// Deliberately NO prize copy here: prize display returns only when a
// contest-level prize field exists and is > 0, so the no-prize honesty guard
// (no "win money" surfaces on free-to-play contests) is code, not a copy rule.

export interface ContestSummaryInput {
    competitionName?: string | null;
    isWeekendContest?: boolean | null;
    // Kickoff datetimes of the contest's matches (ApiMatchInfo.match_date).
    matchDates?: (string | null | undefined)[];
    // contestDetails.start_time - used until matches load.
    fallbackStartTime?: string | null;
}

export interface ContestSummary {
    // Contextual title without dates ("Premier League Weekend"). This is the
    // primary label in compact in-contest chrome.
    title: string | null;
    // Human-readable slate date/range ("Aug 22–23").
    dateLabel: string | null;
    // "Premier League Weekend Aug 22–23" / "Premier League · Aug 22"
    label: string | null;
    // "First kickoff Sat 10:00 AM" (device-local time)
    firstKickoffLabel: string | null;
    // True once the earliest kickoff has passed - flips the strip from
    // summary to the live scoreboard (recomputed on each data refresh).
    hasKickedOff: boolean;
}

// The DB may store an abbreviation; render the full name. Unmapped
// competitions pass through as-is ("La Liga" needs no help).
const COMPETITION_DISPLAY_NAMES: Record<string, string> = {
    EPL: 'Premier League',
};

const fmtMonthDay = (d: Date) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

const fmtDateRange = (first: Date, last: Date): string => {
    const sameDay = first.getFullYear() === last.getFullYear()
        && first.getMonth() === last.getMonth()
        && first.getDate() === last.getDate();
    if (sameDay) return fmtMonthDay(first);

    const sameMonth = first.getFullYear() === last.getFullYear()
        && first.getMonth() === last.getMonth();
    if (sameMonth) {
        const month = first.toLocaleDateString('en-US', { month: 'short' });
        return `${month} ${first.getDate()}–${last.getDate()}`;
    }

    return `${fmtMonthDay(first)}–${fmtMonthDay(last)}`;
};

export function buildContestSummary(input: ContestSummaryInput): ContestSummary {
    const dates = (input.matchDates ?? [])
        .filter((s): s is string => !!s)
        .map((s) => new Date(s))
        .filter((d) => !Number.isNaN(d.getTime()))
        .sort((a, b) => a.getTime() - b.getTime());

    const fallback = input.fallbackStartTime ? new Date(input.fallbackStartTime) : null;
    const first = dates[0] ?? (fallback && !Number.isNaN(fallback.getTime()) ? fallback : null);
    const last = dates.length > 0 ? dates[dates.length - 1] : first;

    const compRaw = input.competitionName?.trim() || null;
    const comp = compRaw ? (COMPETITION_DISPLAY_NAMES[compRaw] ?? compRaw) : null;

    let range: string | null = null;
    if (first && last) {
        range = fmtDateRange(first, last);
    }

    const title = comp ? (input.isWeekendContest ? `${comp} Weekend` : comp) : null;

    let label: string | null = null;
    if (comp && range) {
        label = input.isWeekendContest ? `${title} ${range}` : `${comp} · ${range}`;
    } else {
        label = comp ?? range;
    }

    const firstKickoffLabel = first
        ? `First kickoff ${first.toLocaleDateString('en-US', { weekday: 'short' })} ${first.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`
        : null;

    return {
        title,
        dateLabel: range,
        label,
        firstKickoffLabel,
        hasKickedOff: !!first && first.getTime() <= Date.now(),
    };
}
